import React, { useEffect, useState } from "react";
import axios from "axios";
import appConstants from "../constants/appConstants";
import { Modal, Button, Form } from "react-bootstrap";
import { FaEdit, FaPlus } from "react-icons/fa";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

const getAuthToken = () => localStorage.getItem("authToken");

const TestsPage = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterBatch, setFilterBatch] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subject_id: "",
    exam_date: "",
    type_id: "",
    batch_id: "",
    total_mark: "",
  });

  const [compareBatchId, setCompareBatchId] = useState("");
  const [availableTests, setAvailableTests] = useState([]);
  const [selectedTestIds, setSelectedTestIds] = useState([]);

  useEffect(() => {
    if (compareBatchId) {
      const batchTests = tests.filter((t) => t.batch_id == compareBatchId);
      setAvailableTests(batchTests);
    } else {
      setAvailableTests([]);
      setSelectedTestIds([]);
    }
  }, [compareBatchId, tests]);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const [testRes, subjectRes, typeRes, batchRes] = await Promise.all([
        axios.get(`${appConstants.API_ROOT_URL}get-tests`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${appConstants.API_ROOT_URL}get-subjects`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${appConstants.API_ROOT_URL}get-exam-types`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${appConstants.API_ROOT_URL}get-batches`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTests(testRes.data.data);
      setSubjects(subjectRes.data.data);
      setExamTypes(typeRes.data.data);
      setBatches(batchRes.data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openEnterTestMarksPage = (test) => {
    navigate(`/test-mark-entry/${test.id}`);
  };

  const filteredTests = tests.filter((test) => {
    return (
      (!filterType || test.type_id == filterType) &&
      (!filterSubject || test.subject_id == filterSubject) &&
      (!filterBatch || test.batch_id == filterBatch)
    );
  });

  const openModal = (test = null) => {
    if (test) {
      setEditData(test);
      setFormData({ ...test });
    } else {
      setEditData(null);
      setFormData({
        name: "",
        subject_id: "",
        exam_date: "",
        type_id: "",
        batch_id: "",
        total_mark: "",
      });
    }
    setShowModal(true);
  };

  const handleDeleteTest = async (testId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this test and all its marks?"
      )
    )
      return;

    const token = localStorage.getItem("authToken");
    if (!token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}delete-test?test_id=${testId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        //alert("Test deleted successfully.");
        fetchData(); // Refresh the list
      } else {
        // Handle API-specific errors
        alert(response.data.message || "Failed to delete test.");
      }
    } catch (error) {
      // Handle specific types of errors
      if (error.response) {
        // Server responded with a status other than 2xx
        console.error("Error response from server:", error.response);
        alert(
          `Server error: ${
            error.response.data.message ||
            "An error occurred while deleting the test."
          }`
        );
      } else if (error.request) {
        // No response received from the server
        console.error("No response from server:", error.request);
        alert("Network error: Please check your internet connection.");
      } else {
        // Something else happened
        console.error("Error setting up the request:", error.message);
        alert(`Unexpected error: ${error.message}`);
      }
    }
  };

  const handleViewTest = (test) => {
    navigate(`/test-marks-report/${test.id}`);
  };

  const handleSubmit = async () => {
    const token = getAuthToken();
    if (!token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    const url = editData
      ? `${appConstants.API_ROOT_URL}edit-test`
      : `${appConstants.API_ROOT_URL}create-test`;

    try {
      const response = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        fetchData();
        setShowModal(false);
      } else {
        alert(
          response.data.message || "Something went wrong. Please try again."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert(
          "Unexpected error occurred. Please check your connection and try again."
        );
      }
      console.error("Error saving test:", error);
    }
  };

  const handleCompareMarks = () => {
    if (!compareBatchId || selectedTestIds.length < 2) {
      alert("Please select a batch and at least two tests to compare.");
      return;
    }

    navigate(
      `/compare-marks?batch=${compareBatchId}&tests=${selectedTestIds.join(
        ","
      )}`
    );
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          <FaEdit className="me-2" />
          Tests
        </h4>
        <Button variant="primary" onClick={() => openModal()}>
          <FaPlus className="me-2" />
          Create Test
        </Button>
      </div>

      <div className="card p-3 mb-4">
        <h5 className="mb-3">Compare Student Marks</h5>
        <div className="row">
          <div className="col-md-4">
            <Form.Select
              value={compareBatchId}
              onChange={(e) => setCompareBatchId(e.target.value)}
            >
              <option value="">Select Batch</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.class_name} {b.division}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-md-6">
            <Form.Select
              multiple
              value={selectedTestIds}
              onChange={(e) =>
                setSelectedTestIds(
                  Array.from(e.target.selectedOptions, (opt) => opt.value)
                )
              }
            >
              {availableTests.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}({t.subject_name}) ({t.exam_date})
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-md-2">
            <Button
              variant="info"
              disabled={selectedTestIds.length < 2}
              onClick={() => handleCompareMarks()}
            >
              Compare
            </Button>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <Form.Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {examTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="col-md-4">
          <Form.Select
            value={filterBatch}
            onChange={(e) => setFilterBatch(e.target.value)}
          >
            <option value="">All Batches</option>
            {batches.map((t) => (
              <option key={t.id} value={t.id}>
                {t.class_name} {t.division}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="col-md-4">
          <Form.Select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
          >
            <option value="">All Subjects</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            <th>Name</th>
            <th>Batch</th>
            <th>Subject</th>
            <th>Type</th>
            <th>Date</th>
            <th>Total Mark</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTests.map((test) => (
            <tr key={test.id}>
              <td>{test.name}</td>
              <td>{test.batch_details}</td>
              <td>{subjects.find((s) => s.id === test.subject_id)?.name}</td>
              <td>{examTypes.find((t) => t.id === test.type_id)?.name}</td>
              <td>{test.exam_date}</td>
              <td>{test.total_mark}</td>
              <td>
                <Button
                  size="sm"
                  variant="warning"
                  onClick={() => openModal(test)}
                >
                  <FaEdit /> Edit
                </Button>{" "}
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => openEnterTestMarksPage(test)}
                >
                  Enter Marks
                </Button>{" "}
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => handleViewTest(test)}
                >
                  View
                </Button>{" "}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteTest(test.id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editData ? "Edit Test" : "Create Test"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Select
                value={formData.subject_id}
                onChange={(e) =>
                  setFormData({ ...formData, subject_id: e.target.value })
                }
              >
                <option value="">Select</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={formData.type_id}
                onChange={(e) =>
                  setFormData({ ...formData, type_id: e.target.value })
                }
              >
                <option value="">Select</option>
                {examTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Batch</Form.Label>
              <Form.Select
                value={formData.batch_id}
                onChange={(e) =>
                  setFormData({ ...formData, batch_id: e.target.value })
                }
              >
                <option value="">Select</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.class_name} {b.division}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.exam_date}
                onChange={(e) =>
                  setFormData({ ...formData, exam_date: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Total Mark</Form.Label>
              <Form.Control
                type="number"
                value={formData.total_mark}
                onChange={(e) =>
                  setFormData({ ...formData, total_mark: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default TestsPage;
